"use client";

import React, { useEffect, useRef, useState } from "react";
import { TrendingDown, Scale, Timer, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const benefits = [{
  title: "Ušteda troškova",
  description: "Reparacija je do 50% povoljnija od kupnje novog originalnog paketa. Zadržite originalno kućište i elektroniku uz ugradnju potpuno novih, visokokvalitetnih ćelija.",
  icon: TrendingDown
}, {
  title: "Vrhunska kvaliteta",
  description: "Koristimo isključivo premium ćelije brendova Samsung, LG i Panasonic. Svaka baterija prolazi kroz rigoroznu BMS dijagnostiku i testiranje kapaciteta.",
  icon: Scale
}, {
  title: "Ekološka održivost",
  description: "Smanjite elektronički otpad i CO2 otisak. Obnavljanjem baterije produljujete životni vijek svog romobila i izravno doprinosite očuvanju okoliša.",
  icon: Timer
}];
export default function LixrForBusiness() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="224732" id="zašto-obnoviti-bateriju" className="bg-background text-foreground relative py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-background/95 z-10 backdrop-blur-[1px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background z-10" />
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576390/img/lixr_pro_bar_background.png" alt="ChargeZone Battery Service Workshop" className="w-full h-full object-cover opacity-10" />
      </div>

      <div className="container relative z-20 mx-auto px-4 md:px-6">
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-default font-bold mb-6 text-foreground tracking-tighter">
            Zašto obnoviti <span className="text-primary-foreground">bateriju?</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed">
            Reparacija baterije nije samo ekonomski isplativija opcija, već i tehnički superiorno rješenje koje vašem romobilu vraća tvornički doseg i pouzdanost.
          </p>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {benefits.map((benefit, i) => <div key={i} data-index={i} className={`transition-all duration-700 ease-out delay-${i * 200} ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <Card className="h-full bg-card/60 backdrop-blur-sm border-border/50 hover:border-primary-foreground/50 transition-all duration-300 shadow-xl shadow-primary/5 rounded-md">
                <CardHeader className="pb-4">
                  <div className="w-14 h-14 rounded-md bg-accent flex items-center justify-center mb-6 border border-primary-foreground/20 shadow-sm">
                    <benefit.icon className="w-7 h-7 text-accent-foreground" strokeWidth={1.5} />
                  </div>
                  <CardTitle className="text-2xl font-default font-bold text-foreground tracking-tight">
                    {benefit.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-default leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            </div>)}
        </div>

        {}
        <div ref={ctaRef} className={`mt-20 text-center transition-all duration-1000 ease-out delay-300 ${isCtaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex flex-col items-center space-y-6">
            <p className="text-muted-foreground font-default max-w-lg">
              Saznajte više o našem procesu dijagnostike i reparacije koji jamči dugovječnost vašeg romobila.
            </p>
            <Button asChild size="lg" className="rounded-md px-8 h-14 text-base group bg-primary-foreground text-primary hover:opacity-90 transition-opacity font-default font-semibold">
              <Link to="#">
                Servis baterija
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}